import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast({
        title: "Check your email",
        description:
          "If an account exists, a reset link has been sent to that email.",
      });
      setEmail("");
    } catch (err) {
      toast({
        title: "Request failed",
        description: err?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" data-testid="forgot-password-page">
      <div className="mx-auto grid min-h-screen max-w-6xl grid-cols-1 gap-8 px-5 py-10 lg:grid-cols-2">
        <div className="flex flex-col justify-center">
          <h1
            className="text-4xl font-semibold tracking-tight text-slate-100 sm:text-5xl lg:text-6xl"
            data-testid="forgot-password-title"
          >
            Reset your password
          </h1>
          <p
            className="mt-4 max-w-xl text-base text-slate-200/75"
            data-testid="forgot-password-subtitle"
          >
            Enter your email and we’ll send a secure reset link.
          </p>
          <div className="mt-6" data-testid="forgot-password-links">
            <Link
              to="/login"
              className="text-sm text-slate-200/80 underline-offset-4 hover:underline"
              data-testid="forgot-password-back-to-login-link"
            >
              Back to login
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-center lg:justify-end">
          <Card className="w-full max-w-md glass" data-testid="forgot-password-card">
            <CardHeader>
              <CardTitle className="text-slate-100" data-testid="forgot-password-card-title">
                Forgot password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <label className="text-sm text-slate-200/80" data-testid="forgot-password-email-label">
                    Email
                  </label>
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    type="email"
                    autoComplete="email"
                    className="bg-white/5 text-slate-100 placeholder:text-slate-400/70 ring-1 ring-white/10"
                    data-testid="forgot-password-email-input"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-indigo-500/90 text-white hover:bg-indigo-500"
                  data-testid="forgot-password-submit-button"
                >
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
